import { useMemo } from 'react'
import { useConfigurationStore } from '@stores'

import GameSettings from './Tabs/GameSettings/GameSettings'
import BetSettings from './Tabs/BetSettings'
import AutoplaySettings from './Tabs/AutoplaySettings/AutoplaySettings'
import Information from './Tabs/Information'
import History from './Tabs/History'
import Replay from './Tabs/Replay'

import gameSettingsSelectedIcon from '@assets/menu/icon-settings.svg'
import gameSettingsSelectedInactiveIcon from '@assets/menu/icon-settings-inactive.svg'
import betOptionsIcon from '@assets/menu/icon-coins.svg'
import betOptionsInactiveIcon from '@assets/menu/icon-coins-inactive.svg'
import autoplaySettingsIcon from '@assets/menu/icon-auto-play.svg'
import autoplaySettingsInactiveIcon from '@assets/menu/icon-auto-play-inactive.svg'
import informationIcon from '@assets/menu/icon-information.svg'
import informationInactiveIcon from '@assets/menu/icon-information-inactive.svg'
import historyIcon from '@assets/menu/icon-history.svg'
import historyInactiveIcon from '@assets/menu/icon-history-inactive.svg'
import replayIcon from '@assets/menu/icon-replay.svg'
import replayInactiveIcon from '@assets/menu/icon-replay-inactive.svg'
import { MenuSectionsIds } from 'game-ui-types'
import { useTranslation } from '@amusnet/translations/react'

export const useMenuTabs = () => {
    const { t } = useTranslation()
    const shouldShowGameHistory = useConfigurationStore((state) => state.shouldShowGameHistory)
    const shouldShowBetSettingsSection = useConfigurationStore((state) => state.shouldShowBetSettingsSection)
    const isAutoplayDisabled = useConfigurationStore((state) => state.isAutoplayDisabled)
    const shouldShowReplay = useConfigurationStore((state) => state.shouldShowReplay)
    const commonPaddingTop = 'pt-[34px]'

    return useMemo(() => {
        const tabs = [
            {
                key: MenuSectionsIds.GAME_SETTINGS,
                title: t('gameui.gameSettings'),
                component: <GameSettings />,
                shouldShowBalanceAndBet: false,
                iconSelected: gameSettingsSelectedIcon,
                iconUnselected: gameSettingsSelectedInactiveIcon,
                componentContainerClassName: commonPaddingTop,
                id: 'game-settings-tab',
            },
            {
                key: MenuSectionsIds.BET_SETTINGS,
                title: t('gameui.betSettings'),
                component: <BetSettings />,
                shouldShowBalanceAndBet: false,
                iconSelected: betOptionsIcon,
                iconUnselected: betOptionsInactiveIcon,
                componentContainerClassName: commonPaddingTop,
                id: 'bet-settings-tab',
            },
            {
                key: MenuSectionsIds.AUTOPLAY_SETTINGS,
                title: t('gameui.autoplaySettings'),
                component: <AutoplaySettings />,
                shouldShowBalanceAndBet: true,
                iconSelected: autoplaySettingsIcon,
                iconUnselected: autoplaySettingsInactiveIcon,
                componentContainerClassName: commonPaddingTop,
                id: 'autoplay-settings-tab',
            },
            {
                key: MenuSectionsIds.INFORMATION,
                title: t('gameui.information'),
                component: <Information />,
                shouldShowBalanceAndBet: false,
                shouldMountComponentIndefinitely: true,
                iconSelected: informationIcon,
                iconUnselected: informationInactiveIcon,
                id: 'information-tab',
            },
            {
                key: MenuSectionsIds.HISTORY,
                title: t('gameui.history'),
                component: <History />,
                shouldShowBalanceAndBet: false,
                iconSelected: historyIcon,
                iconUnselected: historyInactiveIcon,
                componentContainerClassName: commonPaddingTop,
                id: 'history-tab',
            },
            {
                key: MenuSectionsIds.REPLAY,
                title: t('gameui.replay'),
                component: <Replay />,
                shouldShowBalanceAndBet: false,
                iconSelected: replayIcon,
                iconUnselected: replayInactiveIcon,
                componentContainerClassName: '',
                id: 'replay-tab',
            },
        ]

        // 🔥 Filter out hidden tabs automatically
        return tabs.filter((tab) => {
            if (tab.key === MenuSectionsIds.HISTORY && !shouldShowGameHistory) return false

            if (tab.key === MenuSectionsIds.BET_SETTINGS && !shouldShowBetSettingsSection) return false

            if (tab.key === MenuSectionsIds.AUTOPLAY_SETTINGS && isAutoplayDisabled) return false

            if (tab.key === MenuSectionsIds.REPLAY && !shouldShowReplay) return false

            return true
        })
    }, [isAutoplayDisabled, shouldShowBetSettingsSection, shouldShowGameHistory, shouldShowReplay])
}
